import { useLocalization } from '@lib/localization';
import { Box, Button, Divider, IconButton, InputBase, Link, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { type ReactNode } from 'react';
import { SearchIcon, TimesIcon } from 'react-line-awesome';
import {
  barStyles, bulkActionStyles, bulkDangerStyles, countStyles, searchStyles, segmentedStyles, tableText
} from './DataTable.styles';

/** The bar across the top of a table's card: the search on the left, the segments and its tools on the right. */
export const TableBar = ({ children }: { children: ReactNode }) => (
  <Box sx={barStyles}>{children}</Box>
);

interface TableSegment {
  value: string;
  label: string;
  /** Left out until it is known, rather than said as 0. */
  count?: number;
}

interface TableSegmentsProps {
  segments: TableSegment[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * Ready-made cuts of the rows, each with how many rows it holds, one of them always on. They
 * narrow the one list rather than open another, so they are a segmented control and not tabs.
 */
export const TableSegments = ({ segments, value, onChange }: TableSegmentsProps) => (
  <ToggleButtonGroup
    exclusive
    value={value}
    // A press on the segment that is on would leave none on.
    onChange={(_event, next: string | null) => next && onChange(next)}
    sx={segmentedStyles}
  >
    {segments.map((segment) => (
      <ToggleButton key={segment.value} value={segment.value}>
        {segment.label}
        {segment.count !== undefined && <Box component="span" sx={countStyles}>{segment.count}</Box>}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

interface TableSearchProps {
  value: string;
  placeholder: string;
  /** As long as what the search is sent to takes. */
  maxLength?: number;
  onChange: (value: string) => void;
}

/**
 * The table's search, at the start of its bar. It searches as it is typed in, so its icon
 * only says what the field is for; a typed search is emptied by its button or by Escape.
 */
export const TableSearch = ({ value, placeholder, maxLength, onChange }: TableSearchProps) => {
  const { t } = useLocalization();

  return (
    <InputBase
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && value) onChange('');
      }}
      placeholder={placeholder}
      slotProps={{ input: { 'aria-label': placeholder, maxLength } }}
      startAdornment={<SearchIcon className="dt-search-icon" aria-hidden />}
      endAdornment={value && (
        <IconButton size="small" aria-label={t('DataTable_ClearSearch')} onClick={() => onChange('')}>
          <TimesIcon />
        </IconButton>
      )}
      sx={searchStyles}
    />
  );
};

interface BulkBarProps {
  /** How many rows are picked. */
  count: number;
  /** How many rows could be picked: all the search leaves, or on a server's page, the page's. */
  available: number;
  onSelectAll: () => void;
  onClear: () => void;
  /** What can be done to the rows picked. */
  children: ReactNode;
}

/**
 * Stands in the bar's place while rows are picked: how many, the way to pick every row that can
 * be (`available`), what can be done to them, and the way out.
 */
export const BulkBar = ({ count, available, onSelectAll, onClear, children }: BulkBarProps) => {
  const { t } = useLocalization();

  return (
    // Keeps the bar's own colour: what it holds says rows are picked.
    <Box sx={[barStyles, { pl: 1 }]}>
      <IconButton size="small" aria-label={t('DataTable_ClearSelection')} onClick={onClear}>
        <TimesIcon />
      </IconButton>
      <Box component="span" sx={{ fontWeight: 500, color: tableText.primary }}>{t('DataGrid_RowsSelected', { count })}</Box>
      {count < available && (
        <Link component="button" type="button" underline="hover" onClick={onSelectAll} sx={{ fontSize: 13 }}>
          {t('DataTable_SelectAll', [available])}
        </Link>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {children}
    </Box>
  );
};

interface BulkActionProps {
  icon: ReactNode;
  /** Red in its words, for what cannot be taken back. */
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}

/** One thing that can be done to the rows picked, in the bar for them. */
export const BulkAction = ({ icon, danger, onClick, children }: BulkActionProps) => (
  <Button color="inherit" startIcon={icon} onClick={onClick} sx={[bulkActionStyles, Boolean(danger) && bulkDangerStyles]}>
    {children}
  </Button>
);

/** Sets apart the groups of a bulk bar's actions, such as single sign-on's from the account's own. */
export const BulkDivider = () => <Divider orientation="vertical" flexItem sx={{ my: 1.25, mx: 0.5 }} />;
